import { useState } from "preact/hooks";
import { api } from "../../lib/api-client";
import { useToast } from "../../lib/use-toast";
import { DEFAULT_TEMPLATE } from "../../lib/frontmatter-template";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Field,
  SectionHeading,
  Textarea,
} from "../ui";
import type { ProjectDto } from "@shared/types";

type Props = {
  project: ProjectDto;
  onProjectUpdate: (next: ProjectDto) => void;
};

export function FrontmatterTemplateSection({ project, onProjectUpdate }: Props) {
  const [input, setInput] = useState(project.frontmatterTemplate ?? "");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const toast = useToast();

  async function onSave(e: Event) {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    try {
      const next = await api.updateProject(project.id, {
        frontmatterTemplate: input.trim() === "" ? null : input,
      });
      onProjectUpdate(next);
      setInput(next.frontmatterTemplate ?? "");
      toast.success("保存しました");
    } catch (err) {
      setSaveError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  const unchanged = (project.frontmatterTemplate ?? "") === input;

  return (
    <div class="space-y-4">
      <SectionHeading
        level={3}
        title="新規ページのテンプレート"
        actions={
          project.frontmatterTemplate && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setInput("")}
            >
              デフォルトに戻す
            </Button>
          )
        }
      />

      <Card>
        <CardBody class="space-y-3">
          <form onSubmit={onSave} class="space-y-3">
            <Field
              label="frontmatter とボディの初期値"
              hint={
                <>
                  プレースホルダ:{" "}
                  <code class="rounded bg-bg-inset px-1 py-0.5 font-mono text-[11px] text-fg-muted">{`{{publishedAt}}`}</code>
                  {" "}は作成時刻、{" "}
                  <code class="rounded bg-bg-inset px-1 py-0.5 font-mono text-[11px] text-fg-muted">{`{{slug}}`}</code>
                  {" "}は新規 slug に置換されます。空にすると組み込みのデフォルトを使用します。
                </>
              }
            >
              <Textarea
                value={input}
                onInput={(e) =>
                  setInput((e.target as HTMLTextAreaElement).value)
                }
                placeholder={DEFAULT_TEMPLATE}
                spellcheck={false}
                rows={14}
                monospace
                class="text-xs leading-5"
              />
            </Field>
            <div class="flex flex-wrap items-center gap-2">
              <Button
                type="submit"
                variant="secondary"
                loading={saving}
                disabled={saving || unchanged}
              >
                {saving ? "保存中…" : "テンプレートを保存"}
              </Button>
            </div>
            {saveError && (
              <Alert tone="danger">
                {saveError}
              </Alert>
            )}
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
